/**
 * Side-sheet for creating a new V2 room (lightweight "skeleton" form — name,
 * rent, size, floor only). Status, images, description, KeyNest, and bank
 * overrides stay edit-only via RoomEditSheet; use that after creation to fill
 * those in.
 *
 * Mirrors ApartmentEditSheet's create-mode conventions: sectioned Card
 * layout, FormState + patch(), parseOptionalNumber/parseRequiredNumber with
 * toast on invalid input, ⌘+Enter to save.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseOptionalNumber, parseRequiredNumber } from "@/lib/v2-numeric";
import { formatSupabaseError } from "@/lib/format-supabase-error";
import { useCreateRoom } from "@/hooks/v2/useProperties";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: number;
  /** Pre-fill the room's apartment when created from an apartment context. */
  apartmentId?: number | null;
};

type FormState = {
  title: string;
  price: string;
  size_sqm: string;
  floor: string;
};

const EMPTY: FormState = {
  title: "",
  price: "",
  size_sqm: "",
  floor: "",
};

export function RoomCreateSheet({
  open,
  onOpenChange,
  propertyId,
  apartmentId,
}: Props) {
  const { toast } = useToast();
  const { t } = useLanguage();
  const createMut = useCreateRoom();

  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  function patch(p: Partial<FormState>) {
    setForm((f) => ({ ...f, ...p }));
  }

  async function handleSubmit() {
    if (!form.title.trim()) {
      toast({
        title: t(
          "admin.apartments.missingRequiredField",
          "Missing required field"
        ),
        description: t(
          "admin.rooms.roomNameRequired",
          "Room name is required."
        ),
        variant: "destructive",
      });
      return;
    }

    // rooms.price is NOT NULL — default empty input to 0, same fallback the
    // wizard and RoomEditSheet use.
    const price = parseRequiredNumber(form.price, 0);
    if (!price.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.rooms.invalidRent",
          'Rent "{{value}}" is not a valid number. Use digits only (e.g. 650).'
        ).replace("{{value}}", price.raw),
        variant: "destructive",
      });
      return;
    }
    const sizeSqm = parseOptionalNumber(form.size_sqm);
    if (!sizeSqm.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.rooms.invalidSize",
          'Size "{{value}}" is not a valid number. Use digits only (e.g. 18).'
        ).replace("{{value}}", sizeSqm.raw),
        variant: "destructive",
      });
      return;
    }

    try {
      await createMut.mutateAsync({
        property_id: propertyId,
        apartment_id: apartmentId ?? undefined,
        title: form.title.trim(),
        price: price.value ?? 0,
        size_sqm: sizeSqm.value,
        floor: form.floor.trim() || null,
        // This skeleton form has no photos or description — created hidden
        // so it can't reach the public listing as an empty, priceless
        // "available" room. Unhide it from the room detail page once it's
        // fleshed out via Edit.
        is_hidden: true,
      });

      toast({
        title: t("admin.rooms.roomCreatedTitle", "Room created"),
        description: t(
          "admin.rooms.roomCreatedHiddenNote",
          "{{title}} — hidden from public listings until you add photos and unhide it."
        ).replace("{{title}}", form.title.trim()),
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t("admin.rooms.couldNotCreateRoom", "Could not create room"),
        description: formatSupabaseError(err),
        variant: "destructive",
      });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void handleSubmit();
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        onKeyDown={handleKeyDown}
      >
        <SheetHeader className="space-y-1">
          <SheetTitle>{t("admin.rooms.addRoom", "Add room")}</SheetTitle>
          <SheetDescription>
            {t(
              "admin.rooms.addRoomDesc",
              "Creates a bare room, hidden from public listings until you add photos and unhide it via Edit. Press"
            )}{" "}
            <kbd className="font-mono text-[10px]">⌘ ↵</kbd>{" "}
            {t("admin.apartments.toSave", "to save.")}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          <Section title={t("admin.rooms.roomColumn", "Room")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={t("admin.rooms.nameField", "Name")}
                required
                className="sm:col-span-2"
              >
                <Input
                  value={form.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  placeholder={t(
                    "admin.rooms.roomNamePlaceholder",
                    "e.g. Bright corner room"
                  )}
                  autoFocus
                />
              </Field>
              <Field label={t("admin.rooms.rentEurMo", "Rent (€/mo)")}>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) => patch({ price: e.target.value })}
                  placeholder="0"
                />
              </Field>
              <Field label={t("admin.apartments.sizeSqm", "Size (m²)")}>
                <Input
                  type="number"
                  min={0}
                  step="0.1"
                  inputMode="decimal"
                  value={form.size_sqm}
                  onChange={(e) => patch({ size_sqm: e.target.value })}
                />
              </Field>
              <Field label={t("admin.apartments.floor", "Floor")}>
                <Input
                  value={form.floor}
                  onChange={(e) => patch({ floor: e.target.value })}
                  placeholder="2nd"
                />
              </Field>
            </div>
          </Section>
        </div>

        <SheetFooter className="mt-6 gap-2 sticky bottom-0 bg-background pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={createMut.isPending}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={createMut.isPending}
          >
            {createMut.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.saving", "Saving…")}
              </>
            ) : (
              t("admin.rooms.createRoom", "Create room")
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-muted/30 px-4 py-4 space-y-3">
      <header className="space-y-0.5">
        <h3 className="text-sm font-semibold">{title}</h3>
      </header>
      {children}
    </section>
  );
}

function Field({
  label,
  required,
  className,
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ""}`}>
      <Label className="flex items-center gap-1 text-xs font-medium">
        {label}
        {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}
